// src/stores/authSlice.tsx
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Agent, SuperVisor, User } from "../types";
import axiosInstance from "../services/axiosInstance";

// Define AuthState and LoginActionPayload interfaces
interface AuthState {
  user: User | SuperVisor | Agent | null;
  accessToken: string | null;
  refreshToken: string | null;
}

interface LoginActionPayload {
  user: User | SuperVisor | Agent;
  accessToken: string;
  refreshToken: string;
}

// Helper function to validate parsed auth data
const isValidAuthState = (data: any): data is AuthState => {
  return (
    data &&
    typeof data === "object" &&
    ("user" in data ? (data.user === null || typeof data.user === "object") : true) &&
    ("accessToken" in data ? (data.accessToken === null || typeof data.accessToken === "string") : true) &&
    ("refreshToken" in data ? (data.refreshToken === null || typeof data.refreshToken === "string") : true)
  );
};

// Load persisted auth state from localStorage with try-catch and type checking
let initialState: AuthState = { user: null, accessToken: null, refreshToken: null };
const storedAuth = localStorage.getItem("authState");
if (storedAuth) {
  try {
    const parsed = JSON.parse(storedAuth);
    if (isValidAuthState(parsed)) {
      initialState = parsed;
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${parsed.accessToken}`;
    }
  } catch (error) {
    localStorage.removeItem("authState"); // Clear invalid data
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Login reducer: updates state with user and token info
    login: (state, action: PayloadAction<LoginActionPayload>) => {
      const { accessToken, refreshToken, user } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      localStorage.setItem("authState", JSON.stringify(state));
    },
    // Logout reducer: clears auth state
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      localStorage.removeItem("authState");
      delete axiosInstance.defaults.headers.common["Authorization"];
    },
    // UpdateUser reducer: updates user object in auth state
    updateUser: (state, action: PayloadAction<User | SuperVisor | Agent>) => {
      state.user = { ...state.user, ...action.payload }
      localStorage.setItem("authState", JSON.stringify(state));
    },
  },
});

export const { login, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;
